/**
 * ColorSubMenu — Exterior color picker panel.
 *
 * Renders inside <SubMenuPanel> when activeSubMenu === 'color'.
 *
 * Visual spec (08-ui-screens.md — Color/Rim SubMenu):
 *   • Horizontal scroll container with momentum + snap centering
 *   • Each swatch: 40px round circle, filled with colorCode
 *   • Lacquer sheen overlay: colorButtonBG.svg (transparent PNG-like SVG)
 *   • Active swatch: white ring border + white glow
 *   • All swatches: drop shadow  shadow-[0_2px_8px_rgba(0,0,0,0.5)]
 *   • Bi-color: diagonal gradient (primary 50% / secondary 50%)
 *   • Fade masks left+right so the list "bleeds" into the edges elegantly
 *   • Spacers on both ends so first/last swatch can be scroll-snapped to center
 *   • Color display name label below swatches — fades on change
 *
 * On tap:
 *   1. Store: setSelectedColor(id)     → triggers useColor → engine.setMaterial() (animated)
 *   2. Scroll: smoothly scrolls the tapped swatch to center
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#color--rim-submenu
 * @see packages/brand-audi/src/hooks/useColor.ts
 */
import { memo, useRef, useState } from 'react';
import { cn } from '../../../utils/cn';
import { useAppStore } from '../../../store/app.store';
import { tracker, EVENTS } from '../../../services/tracking';
import type { ResolvedColorDefinition } from '@ims/shared';

const ICON_LACQUER = '/app/icons/colorButtonBG.svg';

interface ColorSubMenuProps {
  colors: ResolvedColorDefinition[];
}

export const ColorSubMenu = memo(function ColorSubMenu({ colors }: ColorSubMenuProps) {
  const selectedColorId = useAppStore((s) => s.selectedColorId);
  const setSelectedColor = useAppStore((s) => s.setSelectedColor);

  // Keeps track of the displayed name so we can fade between them
  const [displayName, setDisplayName] = useState<string>(() => {
    const active = colors.find((c) => c.id === selectedColorId);
    return active?.displayName ?? colors[0]?.displayName ?? '';
  });
  const [nameFadeKey, setNameFadeKey] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);

  function handleSelect(id: string) {
    if (id === selectedColorId) return;
    const color = colors.find((c) => c.id === id);
    if (!color) return;
    setSelectedColor(id);
    tracker.track(EVENTS.COLOR_CHANGED, { colorId: id });
    // Trigger name fade
    setNameFadeKey((k) => k + 1);
    setDisplayName(color.displayName);
  }

  if (!colors.length) return null;

  return (
    <div className="flex flex-col items-center gap-0 pb-4">
      {/* ── Swatch scroll track ──────────────────────────────────────────── */}
      {/*
        Mask is on the STATIC parent wrapper — not on the scroll container.
        This means the fade zones are fixed relative to the screen,
        so swatches that scroll into the opaque zone are fully visible.
        The scroll container has py-3 so the top/bottom glow ring is never clipped.
        Spacers at both ends give extra scroll room for the first/last swatch.
      */}
      <div
        className="relative w-full"
        style={{
          maskImage:
            'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
        }}
      >
        <div
          ref={scrollRef}
          className="flex items-center gap-8 overflow-x-auto scrollbar-hide w-full py-3"
          style={{
            justifyContent: 'safe center',
            WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
            overscrollBehaviorX: 'contain',
          }}
        >
          {/* Spacers: scroll room so edge swatches can move into the opaque zone */}
          <div className="shrink-0 w-6" aria-hidden />

          {colors.map((color) => {
            const isActive = color.id === selectedColorId;
            const hasTwoTone = Boolean(color.secondary);

            // Background: solid primary, or diagonal gradient for bi-color
            const backgroundStyle = hasTwoTone
              ? {
                  background: `linear-gradient(-45deg, ${color.primary.colorCode} 50%, ${color.secondary!.colorCode} 50%)`,
                }
              : { backgroundColor: color.primary.colorCode };

            return (
              <button
                key={color.id}
                onClick={() => handleSelect(color.id)}
                aria-label={color.displayName}
                aria-pressed={isActive}
                className={cn(
                  // Base — round, fixed size
                  'relative shrink-0 w-10 h-10 rounded-full cursor-pointer',
                  'transition-all duration-200',
                  // Drop shadow on all swatches
                  'shadow-[0_2px_8px_rgba(0,0,0,0.5)]',
                )}
                style={{
                  ...backgroundStyle,
                  // Active: subtle white glow ring — outline so it never clips
                  ...(isActive
                    ? {
                        outline: '2px solid rgba(255,255,255,0.85)',
                        outlineOffset: '2px',
                        boxShadow: '0 0 8px rgba(255,255,255,0.4), 0 2px 8px rgba(0,0,0,0.5)',
                      }
                    : {}),
                }}
              >
                {/* Lacquer sheen overlay — gives the paint bubble effect */}
                <img
                  src={ICON_LACQUER}
                  alt=""
                  aria-hidden
                  draggable={false}
                  className="absolute inset-0 w-full h-full rounded-full pointer-events-none select-none"
                />
              </button>
            );
          })}

          {/* Trailing spacer */}
          <div className="shrink-0 w-6" aria-hidden />
        </div>
      </div>

      {/* ── Active color name ────────────────────────────────────────────── */}
      <p
        key={nameFadeKey}
        className="text-sm font-normal text-white text-center tracking-wide"
        style={{
          animation: 'colorLabelFadeIn 200ms ease forwards',
        }}
      >
        {displayName}
      </p>

      <style>{`
        @keyframes colorLabelFadeIn {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
});
